import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import { getPromptTypeKoreanName } from "../../../common/models/types";
import { formatGender } from "../../../utils/gender";

const PatientInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoLabel = styled.div`
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  font-size: 14px;
  line-height: 1.6;
`;

/**
 * 평문을 렌더하되 줄바꿈(`\n`)만 `<br/>`로 살린다.
 *
 * ⚠️ `html-react-parser`를 쓰지 않는다(계획 item 3 · 4차 검토). 파서로 렌더하면 `diseases`·`allergies`·
 *    `consultationNotes`에 담긴 `<script>` 같은 태그가 sanitize 없이 실행/렌더될 수 있다(본문 경로의
 *    disallowedElements 차단이 이 경로엔 적용되지 않음). React는 텍스트 노드를 자동 이스케이프하므로,
 *    `\n`으로 split해 `<br/>`를 React 노드로 끼우기만 하면 태그는 글자 그대로 보이고(AC-62) 줄바꿈만
 *    산다(AC-63). "이스케이프 순서" 함정이 원인째 사라진다.
 */
function renderMultilineText(text: string): React.ReactNode {
  // String()으로 방어 — patientSnap은 `any`라 필드가 배열 등 비문자열로 올 수 있다. 그대로 .split하면
  // 페이지 전체가 크래시하므로 문자열로 강제한다(백엔드 계약 안정화 전 전이기 보호).
  const lines = String(text).split("\n");
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {line}
    </React.Fragment>
  ));
}

interface PatientInfoDisplayProps {
  patient?: {
    id?: string | number;
    gender?: string;
    diseases?: string;
    allergies?: string;
    consultationNotes?: string;
  };
  age?: string | number;
  promptType?: string;
  /**
   * "환자 번호" 줄을 숨긴다(기본 false — 약사 화면 무영향).
   * 탑재뷰는 백엔드가 id를 sanitize로 지워 실환자도 "신규 환자"로 뜨므로(P-7) 이 줄을 끈다.
   */
  hidePatientNo?: boolean;
}

const PatientInfoDisplay: React.FC<PatientInfoDisplayProps> = ({
  patient,
  age = "-",
  promptType,
  hidePatientNo = false,
}) => {
  return (
    <PatientInfoSection>
      {!hidePatientNo && (
        <InfoLabel>
          환자 번호: {patient?.id ? `환자 ${patient.id}` : "신규 환자"}
        </InfoLabel>
      )}
      <InfoLabel>나이: {age}</InfoLabel>
      <InfoLabel>성별: {formatGender(patient?.gender)}</InfoLabel>
      <InfoLabel>
        검토: {getPromptTypeKoreanName(promptType as any) || promptType || "-"}
      </InfoLabel>
      <InfoLabel>
        질병 정보: {patient?.diseases ? renderMultilineText(patient.diseases) : "-"}
      </InfoLabel>
      <InfoLabel>
        약물 부작용 및 알레르기 정보:{" "}
        {patient?.allergies ? renderMultilineText(patient.allergies) : "-"}
      </InfoLabel>
      {patient?.consultationNotes && (
        <InfoLabel>
          추가 정보: {renderMultilineText(patient.consultationNotes)}
        </InfoLabel>
      )}
    </PatientInfoSection>
  );
};

export default PatientInfoDisplay;
